import { useTranslation } from "react-i18next"

import type { CurrencyCode } from "@/models/currencyCode"
import type { SelectableLanguageCode } from "@/models/i18n"
import { baseNumberColumnDef } from "@/nzds/data-grid"
import {
  defaultCurrencyFormatOptions,
  translateDecimal,
  withYenWholeNumberFormat,
} from "@/utils/decimal"
import { getDisplayValue, getStringValue } from "@/utils/value"

import type { GridColDef, GridValidRowModel } from "@mui/x-data-grid-pro"

interface FormatCostOptions {
  localeCode: SelectableLanguageCode
  useAccountingFormatWhenNegative?: boolean
}

interface UseCostColumnOptions<TRow extends GridValidRowModel> {
  currencyField?: string
  field?: string
  filterable?: boolean
  headerName?: string
  maxWidth?: number
  minWidth?: number
  renderCell?: GridColDef<TRow>["renderCell"]
  sortable?: boolean
  useAccountingFormatWhenNegative?: boolean
}

export const formatCost = (
  value: string | number | null | undefined,
  currency: string | null | undefined,
  { localeCode, useAccountingFormatWhenNegative = true }: FormatCostOptions
): string => {
  const normalized = getStringValue(value)
  if (normalized == null || normalized.trim() === "") {
    return getDisplayValue(value)
  }

  if (!currency) {
    return getDisplayValue(value)
  }

  return translateDecimal({
    format: withYenWholeNumberFormat({
      currency: currency as CurrencyCode,
      ...defaultCurrencyFormatOptions,
    }),
    localeCode,
    options: {
      useAccountingFormatWhenNegative,
    },
    value,
  })
}

export const useCostColumn = <TRow extends GridValidRowModel>(
  options: UseCostColumnOptions<TRow> = {}
): GridColDef<TRow> => {
  const { i18n, t } = useTranslation("common")
  const currencyField = options.currencyField ?? "currency"
  const field = options.field ?? "cost"
  const filterable = options.filterable ?? false
  const headerName = options.headerName ?? t("fields.cost.title")
  const maxWidth = options.maxWidth ?? 220
  const minWidth = options.minWidth ?? 120
  const renderCell = options.renderCell
  const sortable = options.sortable ?? true
  const useAccountingFormatWhenNegative =
    options.useAccountingFormatWhenNegative ?? true

  return {
    ...baseNumberColumnDef,
    field,
    filterable,
    flex: 1,
    headerName,
    maxWidth,
    minWidth,
    renderCell,
    sortable,
    valueFormatter: (value, row) => {
      const rowRecord = row as Record<string, unknown>
      return formatCost(value, getStringValue(rowRecord[currencyField]), {
        localeCode: i18n.language as SelectableLanguageCode,
        useAccountingFormatWhenNegative,
      })
    },
  }
}
